import { useDimensions } from "@repo/core/hooks/useDimensions";
import { useCallback, useEffect, useRef, useMemo, useState } from "react";
import { IModalRefProps } from "@repo/core/components/layout/Modal/types";
import { IActionsheetRefProps } from "@repo/core/components/layout/Actionsheet/types";
import { useNavigation } from "@repo/core/hooks/useNavigation";
import { ROUTES } from "@repo/core/routes";
import { useRouteParams } from "@repo/core/hooks/useRouteParams";
import { useDeviceType } from "@repo/core/hooks/useDeviceType";
import { useQuery } from "@tanstack/react-query";
import { useRefetchProposalViewInfoQueries } from "@repo/core/hooks/useRefetchProposalViewInfoQueries";
import { normalizeProposalIdParam } from "@repo/core/hooks/useRefetchProposalViewInfoQueries/invalidateProposalViewInfoQueries";
import { ProposalsResource } from "@repo/core/services/resources/proposals";
import { RequisitionsResource } from "@repo/core/services/resources/requisitions";
import { apiInstance } from "@repo/core/services/instances/api";
import { wayupApiInstance } from "@repo/core/services/instances/wayup-api";
import { normalizeProposalSituationCode } from "@repo/core/screens/Proposals/screens/ProposalsSearch/utils";
import { getProposalEffectuationErrorMessage } from "@repo/core/screens/Proposals/rules/proposalEffectuationError";
import {
  getProposalEffectuationFlow,
  isProposalEffectuationProcessingStatus
} from "@repo/core/screens/Proposals/rules/proposalEffectuationFlow";
import { getAnalysisQueueProposalOptions } from "@repo/core/screens/Proposals/rules/analysisQueueRules";
import { effectuateRejectedFidelityProposal } from "@repo/core/screens/Proposals/services/effectuateRejectedFidelityProposal.service";
import {
  mapProposalDetails,
  ProposalDetailsData,
  mapProposalDocuments,
  mapAnalysisReasons,
  collectUniqueStandardRejectionReasons
} from "@repo/core/mappers/proposals";
import {
  ConsultProposalResponseDTO,
  ConsultProposalSituationResponseDTO,
  PROPOSTA_SITUACAO,
  ProposalTypeEnum
} from "@repo/core/services/entities/Proposals";
import { MobileProposalResource } from "@repo/core/services/resources/mobile-proposal";
import { PROPOSAL_ACTIONS } from "@repo/core/constants/proposals";
import { mapProposalAnalysisItems } from "@repo/core/mappers/proposals";
import { ProposalDocument } from "@repo/core/screens/Proposals/screens/ProposalsViewInfo/sections/DocumentsView/types";
import { IDocumentPreviewBottomSheetRefProps } from "./components/DocumentPreviewBottomSheet/types";
import useToast from "@repo/core/components/core/Toast";
import { useFidelityProposal } from "@repo/core/screens/ProposalEffectiveFlows/Fidelity/hooks/useFidelityProposal";
import { useCreditProposal } from "@repo/core/screens/ProposalEffectiveFlows/Credit/hooks/useCreditProposal";
import { usePreProposal } from "@repo/core/screens/PreProposal/hooks/usePreProposal";
import { getCreditTableEntryNavigation } from "@repo/core/screens/Proposals/rules/creditTableCompletionFlow";
import { useUserPermissions } from "@repo/core/hooks/useUserPermissions";
import { canViewProposalDocuments } from "./utils";

const proposalsResource = new ProposalsResource(apiInstance);
const mobileProposalResource = new MobileProposalResource(apiInstance);
const requisitionsResource = new RequisitionsResource(wayupApiInstance);
const ACTION_SHEET_CLOSE_DELAY_MS = 350;

const waitForNextFrame = (): Promise<void> =>
  new Promise((resolve) => {
    requestAnimationFrame(() => resolve());
  });

const waitForActionSheetClose = (): Promise<void> =>
  new Promise((resolve) => {
    setTimeout(resolve, ACTION_SHEET_CLOSE_DELAY_MS);
  });

const PROPOSAL_VIEW_INFO_QUERY_OPTIONS = {
  staleTime: 0,
  cacheTime: 0,
  refetchOnMount: "always" as const,
  refetchOnWindowFocus: "always" as const,
  refetchOnReconnect: "always" as const
};

export const useProposalsViewInfoController = () => {
  const { isMobile } = useDimensions();
  const { isWeb: isWebDevice } = useDeviceType();

  const { clearCreditProposal, setPostSaveAction } = useCreditProposal();
  const { clearFidelityProposal } = useFidelityProposal();
  const { startQueueProposalEdit } = usePreProposal();
  const { permissions } = useUserPermissions();
  const { push, replace } = useNavigation();
  const toast = useToast();

  const { proposalId } = useRouteParams();
  const normalizedProposalId = normalizeProposalIdParam(proposalId);

  useRefetchProposalViewInfoQueries(normalizedProposalId);

  const alertModalRef = useRef<IModalRefProps>(null);
  const alertAnalysisModalRef = useRef<IModalRefProps>(null);
  const actionSheetRef = useRef<IActionsheetRefProps>(null);
  const documentPreviewBottomSheetRef =
    useRef<IDocumentPreviewBottomSheetRefProps>(null);
  const [isEffectuatingProposal, setIsEffectuatingProposal] = useState(false);
  const [loadingOptionAction, setLoadingOptionAction] =
    useState<PROPOSAL_ACTIONS | null>(null);
  const actionInFlightRef = useRef(false);
  const [refreshing, setRefreshing] = useState(false);

  // Fetch proposal details
  const {
    data: proposalData,
    refetch: refetchProposal,
    isFetching: isFetchingProposal,
    error: proposalError
  } = useQuery({
    queryKey: ["proposalDetails", normalizedProposalId],
    queryFn: () => proposalsResource.getProposal(normalizedProposalId || ""),
    select: (data) => data.data as unknown as ConsultProposalResponseDTO,
    enabled: !!normalizedProposalId,
    ...PROPOSAL_VIEW_INFO_QUERY_OPTIONS
  });

  // Fetch proposal situation (analysis items)
  const {
    data: proposalSituationData,
    refetch: refetchSituation,
    isFetching: isFetchingSituation
  } = useQuery({
    queryKey: ["proposalSituation", normalizedProposalId],
    queryFn: () =>
      proposalsResource.getProposalSituation(normalizedProposalId || ""),
    select: (data) =>
      data.data as unknown as ConsultProposalSituationResponseDTO,
    enabled: !!normalizedProposalId,
    ...PROPOSAL_VIEW_INFO_QUERY_OPTIONS
  });

  // Fetch analysis results (Wayup API)
  const {
    data: analysisReasonsData,
    refetch: refetchAnalysisReasons,
    isFetching: isFetchingAnalysisReasons
  } = useQuery({
    queryKey: ["analysisReasons", normalizedProposalId],
    queryFn: () =>
      requisitionsResource.getProposalAnalysisResult(
        normalizedProposalId || ""
      ),
    select: (data) => data.data,
    enabled: !!normalizedProposalId,
    ...PROPOSAL_VIEW_INFO_QUERY_OPTIONS
  });

  const hasFreshProposalData = !!proposalData && !isFetchingProposal;
  const showAnalysisModal =
    hasFreshProposalData &&
    proposalData?.strSituacao === PROPOSTA_SITUACAO.ANALYSIS_MANUAL;
  const canViewDocuments =
    hasFreshProposalData &&
    canViewProposalDocuments(proposalData?.strSituacao, permissions);

  // Fetch proposal documents
  const {
    data: documentsData,
    refetch: refetchDocuments,
    isFetching: isFetchingDocuments
  } = useQuery({
    queryKey: ["proposalDocuments", normalizedProposalId],
    queryFn: () =>
      proposalsResource.getDocumentsList(normalizedProposalId || ""),
    enabled: !!normalizedProposalId && canViewDocuments,
    ...PROPOSAL_VIEW_INFO_QUERY_OPTIONS
  });

  // Map proposal details
  const proposalDetails: ProposalDetailsData | null = useMemo(() => {
    if (!proposalData || !hasFreshProposalData) {
      return null;
    }
    const mappedDetails = mapProposalDetails(
      proposalData,
      proposalSituationData
    );

    // Use analysis reasons from new endpoint if available, otherwise fallback to old endpoint
    if (analysisReasonsData && analysisReasonsData.length > 0) {
      mappedDetails.analysisItems = mapAnalysisReasons(analysisReasonsData);
      mappedDetails.standardRejectionReasons =
        collectUniqueStandardRejectionReasons(analysisReasonsData);
    } else if (proposalSituationData?.lstItensAnalise) {
      mappedDetails.analysisItems = mapProposalAnalysisItems(
        proposalSituationData.lstItensAnalise
      );
    }

    return mappedDetails;
  }, [
    proposalData,
    hasFreshProposalData,
    proposalSituationData,
    analysisReasonsData
  ]);

  // Map documents
  const documents: ProposalDocument[] = useMemo(() => {
    if (!documentsData?.data?.documentos) {
      return [];
    }
    return mapProposalDocuments(documentsData.data.documentos);
  }, [documentsData]);

  const isLoadingProposal =
    !!normalizedProposalId && (refreshing || isFetchingProposal);
  const isLoadingAnalysis =
    !!normalizedProposalId &&
    (refreshing || isFetchingSituation || isFetchingAnalysisReasons);
  const isLoadingDocumentsList =
    canViewDocuments &&
    !!normalizedProposalId &&
    (refreshing || isFetchingDocuments);
  const hasError = !!proposalError;

  const onRefresh = useCallback(async () => {
    setRefreshing(true);

    try {
      await Promise.allSettled([
        refetchProposal(),
        refetchSituation(),
        refetchAnalysisReasons(),
        canViewDocuments ? refetchDocuments() : Promise.resolve()
      ]);
    } finally {
      setRefreshing(false);
    }
  }, [
    canViewDocuments,
    refetchAnalysisReasons,
    refetchDocuments,
    refetchProposal,
    refetchSituation
  ]);

  const onSeeLimit = () => {
    alertModalRef.current?.open();
  };

  const onCloseSeeLimit = () => {
    alertModalRef.current?.close();
  };

  const handleOnSeeMoreDetails = () => {
    // TODO: Add params to push
    push(ROUTES.creditProposalDetails.replace(":id", "9999999"));
  };

  const onPressOpenOptions = () => {
    actionSheetRef.current?.open();
  };

  const onPressEffectiveProposal = async () => {
    if (actionInFlightRef.current) {
      return;
    }

    actionInFlightRef.current = true;
    setIsEffectuatingProposal(true);
    let keepsLockDuringNavigation = false;

    try {
      const status = normalizeProposalSituationCode(proposalData?.strSituacao);

      if (!status || !normalizedProposalId) {
        return;
      }

      const navigateToEffectuationSuccess = () => {
        const primaryEffectuationAction = getAnalysisQueueProposalOptions(
          status,
          proposalData?.strTipoProposta,
          undefined,
          permissions
        ).find(
          (option) =>
            option.action === PROPOSAL_ACTIONS.EFFECTIVE_PROPOSAL ||
            option.action === PROPOSAL_ACTIONS.FIDELITY_EFFECTIVE
        );

        replace(ROUTES.proposalEffectiveSuccess, {
          proposalId: normalizedProposalId,
          isCreditProposal:
            primaryEffectuationAction?.action ===
            PROPOSAL_ACTIONS.EFFECTIVE_PROPOSAL
        });
      };

      const effectuateProposal = async (): Promise<boolean> => {
        actionSheetRef.current?.open();
        await waitForNextFrame();

        let errorMessage: string | null = null;
        let shouldNavigateToSuccess = false;

        try {
          const response =
            status === PROPOSTA_SITUACAO.APPROVED_AUTOMATICALLY &&
            proposalData?.strTipoProposta === ProposalTypeEnum.FIDELITY
              ? await effectuateRejectedFidelityProposal({
                  lngProposta: normalizedProposalId,
                  mobileProposalResource,
                  proposalsResource
                })
              : await proposalsResource.effectuateProposal(
                  normalizedProposalId
                );

          if (response.status === 200) {
            const refreshedProposal = await refetchProposal();
            const refreshedStatus =
              refreshedProposal.data?.strSituacao || proposalData?.strSituacao;

            await Promise.allSettled([
              refetchSituation(),
              refetchAnalysisReasons(),
              canViewDocuments ? refetchDocuments() : Promise.resolve()
            ]);

            if (isProposalEffectuationProcessingStatus(refreshedStatus)) {
              toast.info(
                "Proposta em processamento",
                "A proposta ainda está em análise. Atualize a tela para consultar a nova situação."
              );
              return false;
            }

            shouldNavigateToSuccess = true;
          } else {
            errorMessage = getProposalEffectuationErrorMessage(response);
          }
        } catch (error: unknown) {
          errorMessage = getProposalEffectuationErrorMessage(error);
        } finally {
          actionSheetRef.current?.close();
          await waitForActionSheetClose();
        }

        if (shouldNavigateToSuccess) {
          navigateToEffectuationSuccess();
          return true;
        }

        if (errorMessage) {
          toast.error("Erro ao efetivar proposta", errorMessage);
        }

        return false;
      };

      switch (
        getProposalEffectuationFlow(status, proposalData?.strTipoProposta)
      ) {
        case "creditForm":
          clearCreditProposal();
          push(ROUTES.creditProposalPersonalData, {
            proposalId: normalizedProposalId
          });
          keepsLockDuringNavigation = true;
          break;

        case "fidelityForm":
          clearFidelityProposal();
          push(ROUTES.fidelityProposalCompleteRegistration, {
            proposalId: normalizedProposalId
          });
          keepsLockDuringNavigation = true;
          break;

        case "effectuate":
          keepsLockDuringNavigation = await effectuateProposal();
          break;

        default:
          console.warn(
            `Proposal status ${status} is not mapped for effective proposal action`
          );
          break;
      }
    } finally {
      if (keepsLockDuringNavigation) {
        await waitForActionSheetClose();
      }
      actionInFlightRef.current = false;
      setIsEffectuatingProposal(false);
    }
  };

  const handleOnPressOption = async (action: PROPOSAL_ACTIONS) => {
    if (actionInFlightRef.current) {
      return;
    }

    actionInFlightRef.current = true;
    setLoadingOptionAction(action);
    let keepsLockDuringNavigation = false;

    try {
      switch (action) {
        case PROPOSAL_ACTIONS.SEND_TO_CREDIT_TABLE:
          if (normalizedProposalId) {
            const entryNavigation = getCreditTableEntryNavigation(
              proposalData?.strSituacao,
              normalizedProposalId
            );

            if (entryNavigation.postSaveAction) {
              clearCreditProposal();
              setPostSaveAction(entryNavigation.postSaveAction);
            }

            push(entryNavigation.route, entryNavigation.params);
            keepsLockDuringNavigation = true;
          }
          actionSheetRef.current?.close();
          break;
        case PROPOSAL_ACTIONS.EDIT_PROPOSAL:
          if (normalizedProposalId) {
            actionSheetRef.current?.close();
            await startQueueProposalEdit(normalizedProposalId, "proposal");
            keepsLockDuringNavigation = true;
          }
          break;
        case PROPOSAL_ACTIONS.EDIT_PRE_PROPOSAL:
          if (normalizedProposalId) {
            actionSheetRef.current?.close();
            await startQueueProposalEdit(normalizedProposalId, "preProposal");
            keepsLockDuringNavigation = true;
          }
          break;
        default:
          console.log("action not found");
          break;
      }
    } finally {
      if (keepsLockDuringNavigation) {
        await waitForActionSheetClose();
      }
      actionInFlightRef.current = false;
      setLoadingOptionAction(null);
    }
  };

  const handleBack = () => {
    replace(ROUTES.proposals);
  };

  const handleViewDocument = (document: ProposalDocument) => {
    documentPreviewBottomSheetRef.current?.open(document);
  };

  useEffect(() => {
    //  TODO: only yo mock
    if (showAnalysisModal) {
      alertAnalysisModalRef.current?.open();
    }
  }, [showAnalysisModal]);

  return {
    isMobile,
    alertModalRef,
    alertAnalysisModalRef,
    actionSheetRef,
    onPressEffectiveProposal,
    onPressOpenOptions,
    onSeeLimit,
    handleOnSeeMoreDetails,
    onCloseSeeLimit,
    handleOnPressOption,
    isWebDevice,
    handleBack,
    proposalDetails,
    proposalType: proposalData?.strTipoProposta,
    isLoadingProposal,
    isLoadingAnalysis,
    isLoadingDocuments: isLoadingDocumentsList,
    hasError,
    documents,
    handleViewDocument,
    documentPreviewBottomSheetRef,
    proposalId: normalizedProposalId,
    isEffectuatingProposal,
    loadingOptionAction,
    onRefresh,
    refreshing,
    permissions,
    canViewDocuments
  };
};
